import { useEffect } from "react";

import { AjaxManager } from "@impero-com/common/utils/ajax";
import { setFlashRedirectToCurrentLocation } from "@impero-com/common/utils/flash";
import { withHeader, Meta } from "@impero-com/common/utils/header";
import withI18n, { t } from "@impero-com/common/utils/i18n";
import { Routes, Route, Navigate } from "react-router-dom";

import { emitNotificationOnQueryParam } from "utils/notification";

import ForgotPassword from "../anon/forgotPasswordForm";
import { LoginTypeSwitch } from "../anon/loginTypeSwitch";
import RegisterForm from "../anon/registerForm";
import ResetPassword from "../anon/resetPasswordForm";
import * as theme from "../anon/theme.css";
import { useSsoRedirect } from "../utils/hooks/useSsoRedirect";
import { useOrgDisplayInfo } from "../utils/queries/useOrgDisplayInfoSuspenseQuery";

interface AppProps {
    ajaxManager: AjaxManager;
}

function App({ ajaxManager }: AppProps) {
    const orgCookie = useOrgDisplayInfo();
    useSsoRedirect();

    useEffect(() => {
        handleNotifications();
    }, []);

    return (
        <div className={theme.app}>
            <Routes>
                <Route
                    path="/"
                    element={
                        <>
                            <Meta title={() => t("Login_Title")} />
                            <LoginTypeSwitch
                                ajaxManager={ajaxManager}
                                orgCookie={orgCookie}
                                orgProfileCookie={null}
                            />
                        </>
                    }
                />
                <Route
                    path="/register/:token"
                    element={
                        <>
                            <Meta title={() => t("RegisterUser_Title")} />
                            <RegisterForm
                                ajaxManager={ajaxManager}
                                orgCookie={orgCookie}
                            />
                        </>
                    }
                />
                <Route
                    path="/register"
                    element={
                        <>
                            <Meta title={() => t("RegisterUser_Title")} />
                            <RegisterForm
                                ajaxManager={ajaxManager}
                                orgCookie={orgCookie}
                            />
                        </>
                    }
                />
                <Route
                    path="/reset-password/:token"
                    element={
                        <>
                            <Meta title={() => t("ResetPassword_Title")} />
                            <ResetPassword
                                ajaxManager={ajaxManager}
                                orgCookie={orgCookie}
                            />
                        </>
                    }
                />
                <Route
                    path="/reset-password"
                    element={
                        <>
                            <Meta title={() => t("ResetPassword_Title")} />
                            <ResetPassword
                                ajaxManager={ajaxManager}
                                orgCookie={orgCookie}
                            />
                        </>
                    }
                />
                <Route
                    path="/forgot-password"
                    element={
                        <>
                            <Meta
                                title={() => t("LoginFlow_ForgotYourPassword")}
                            />
                            <ForgotPassword
                                ajaxManager={ajaxManager}
                                orgCookie={orgCookie}
                            />
                        </>
                    }
                />
                <Route path="*" element={<RedirectToLogin />} />
            </Routes>
        </div>
    );
}

function RedirectToLogin() {
    setFlashRedirectToCurrentLocation();
    return <Navigate to="/" replace />;
}

const handleNotifications = () => {
    emitNotificationOnQueryParam(
        "CollectCompleted",
        "True",
        t("CollectControlResult_Completed_Title"),
        "success"
    );
    emitNotificationOnQueryParam(
        "CollectAlreadyComplete",
        "True",
        t("CollectControlResult_AlreadyCompleted_Title"),
        "warning"
    );
    emitNotificationOnQueryParam(
        "CollectArchived",
        "True",
        t("CollectControlResult_Archived_Title"),
        "warning"
    );
    emitNotificationOnQueryParam(
        "ReviewRejected",
        "True",
        t("ReviewResult_Rejected_Title") +
            " - " +
            t("ReviewResult_Rejected_Description"),
        "success"
    );
    emitNotificationOnQueryParam(
        "ReviewApproved",
        "True",
        t("ReviewResult_Approved_Title"),
        "success"
    );
    emitNotificationOnQueryParam(
        "ReviewAlreadyComplete",
        "True",
        t("ReviewResult_AlreadyCompleted_Title"),
        "warning"
    );
    emitNotificationOnQueryParam(
        "ReviewControlNotCompletedYet",
        "True",
        t("ReviewResult_ControlNotCompletedYet_Title"),
        "warning"
    );
    emitNotificationOnQueryParam(
        "ReviewArchived",
        "True",
        t("ReviewResult_Archived_Title") +
            " - " +
            t("ReviewResult_Archived_Description"),
        "warning"
    );
};

export const AppAnonymous = withI18n<AppProps>(withHeader<AppProps>(App));
